import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import DauTrang, { NhanDau } from "components/DauTrang";
import ChonDiaChi from "components/ChonDiaChi";
import { oTieuDeCot, AN_DUOI_MD, AN_DUOI_SM, chuXuongDong } from "lib/kieuBangDuLieu";
import {
  useDialogFullManHinh,
  oDialogContentFormDai,
  oDialogActionsFormDai,
  oDialogPaperFormDai,
  oDialogFormFlex,
} from "lib/dungDialogFullManHinh";
import { hopTacXaApi } from "api/hopTacXa";
import { xuatExcelHopTacXa } from "lib/xuatExcelHopTacXa";
import useTour from "hooks/useTour";
import buocQuanLyHopTacXa from "tours/quanLyHopTacXa";

const RONG = {
  tenHopTacXa: "",
  nguoiDaiDien: "",
  sdt: "",
  diaChi: "",
  email: "",
  tinh: "",
  xa: "",
  tinhMa: "",
  xaMa: "",
};

function ThongTinPhu({ htx }) {
  // Mã HTX vào đầu dòng phụ vì cột Mã bị ẩn dưới md.
  const phan = [htx.maHopTacXa, htx.nguoiDaiDien, htx.sdt, htx.email, htx.diaChi].filter(Boolean);
  if (phan.length === 0) return null;
  return (
    <MDTypography
      variant="caption"
      color="text"
      display={{ xs: "block", md: "none" }}
      mt={0.25}
      lineHeight={1.4}
      sx={chuXuongDong}
    >
      {phan.join(" · ")}
    </MDTypography>
  );
}

ThongTinPhu.propTypes = {
  htx: PropTypes.shape({
    maHopTacXa: PropTypes.string,
    nguoiDaiDien: PropTypes.string,
    sdt: PropTypes.string,
    email: PropTypes.string,
    diaChi: PropTypes.string,
  }).isRequired,
};

// Dải phân nhóm trong Dialog (dùng cho cả "Địa bàn" và "Liên hệ").
function TieuDeNhom({ children, moTa }) {
  return (
    <MDBox
      mt={1}
      mb={0.5}
      sx={({ palette }) => ({ borderTop: `1px solid ${palette.grey[300]}`, pt: 1.5 })}
    >
      <MDTypography variant="overline" color="text">
        {children}
      </MDTypography>
      {moTa ? (
        <MDTypography variant="caption" color="text" display="block" lineHeight={1.4}>
          {moTa}
        </MDTypography>
      ) : null}
    </MDBox>
  );
}

TieuDeNhom.propTypes = {
  children: PropTypes.node.isRequired,
  moTa: PropTypes.string,
};

TieuDeNhom.defaultProps = {
  moTa: "",
};

function QuanLyHopTacXa() {
  const fullManHinh = useDialogFullManHinh();
  const [danhSach, setDanhSach] = useState([]);
  const [dangTai, setDangTai] = useState(true);
  const [loi, setLoi] = useState("");
  const [thongBao, setThongBao] = useState("");
  const [timKiem, setTimKiem] = useState("");

  const [modal, setModal] = useState(null);
  const [dangLuu, setDangLuu] = useState(false);
  const [dangXoaId, setDangXoaId] = useState(null);
  const [dangCapMaId, setDangCapMaId] = useState(null);
  const [dangXuatExcel, setDangXuatExcel] = useState(false);

  async function taiLai() {
    // Realtime onSnapshot cập nhật danh sách.
  }

  useEffect(() => {
    setDangTai(true);
    const huy = hopTacXaApi.langNgheDanhSach(
      (ds) => {
        setDanhSach(ds);
        setDangTai(false);
      },
      (err) => {
        setLoi(err.message);
        setDangTai(false);
      }
    );
    return huy;
  }, []);

  const danhSachHienThi = useMemo(() => {
    const q = timKiem.trim().toLowerCase();
    if (!q) return danhSach;
    return danhSach.filter((htx) =>
      [htx.maHopTacXa, htx.tenHopTacXa, htx.nguoiDaiDien, htx.sdt, htx.email].some((v) =>
        v?.toLowerCase().includes(q)
      )
    );
  }, [danhSach, timKiem]);

  async function xuatExcel() {
    setLoi("");
    setDangXuatExcel(true);
    try {
      await xuatExcelHopTacXa({ danhSach: danhSachHienThi });
      setThongBao("Đã xuất Excel danh sách hợp tác xã.");
    } catch (err) {
      setLoi(err.message || "Không xuất được Excel.");
    } finally {
      setDangXuatExcel(false);
    }
  }

  async function luu(e) {
    e.preventDefault();
    if (!modal.tenHopTacXa.trim()) {
      setLoi("Nhập tên hợp tác xã.");
      return;
    }
    // Tỉnh/Xã chỉ bắt buộc lúc TẠO MỚI vì là thành phần của Mã HTX (sinh 1 lần).
    // Khi sửa HTX cũ (chưa có mã) không ép nhập lại: mã không sinh lại nên bắt
    // buộc ở đây chỉ chặn việc sửa số điện thoại/email mà thôi.
    if (!modal.id && (!String(modal.tinhMa ?? "").trim() || !String(modal.xaMa ?? "").trim())) {
      setLoi("Chọn Tỉnh và Xã để sinh Mã HTX.");
      return;
    }
    setDangLuu(true);
    setLoi("");
    setThongBao("");
    try {
      const { id, ...data } = modal;
      if (id) {
        await hopTacXaApi.capNhat(id, data);
      } else {
        const moi = await hopTacXaApi.taoMoi(data);
        setThongBao(`Đã tạo hợp tác xã, mã: ${moi.maHopTacXa}`);
      }
      setModal(null);
      await taiLai();
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangLuu(false);
    }
  }

  // Chỉ dùng cho HTX tạo TRƯỚC tính năng Mã HTX (chưa từng có mã). Mã sinh 1
  // lần lúc tạo mới nên nút này không hiện lại sau khi đã cấp — xem capMa.
  async function capMa(htx) {
    setDangCapMaId(htx.id);
    setLoi("");
    setThongBao("");
    try {
      const cap = await hopTacXaApi.capMa(htx.id);
      setThongBao(`Đã cấp mã: ${cap.maHopTacXa}`);
      await taiLai();
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangCapMaId(null);
    }
  }

  async function xoa(htx) {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Xoá hợp tác xã "${htx.tenHopTacXa}"?`)) return;
    setDangXoaId(htx.id);
    setLoi("");
    try {
      await hopTacXaApi.xoa(htx.id);
      setDanhSach((ds) => ds.filter((x) => x.id !== htx.id));
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangXoaId(null);
    }
  }

  useTour("quan-ly-hop-tac-xa", buocQuanLyHopTacXa());

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <DauTrang
          nhan="Quản lý"
          tieuDe="Hợp tác xã"
          moTa="Danh sách các hợp tác xã đối tác — mã định danh, tên, người đại diện, liên hệ."
        >
          <MDBox display="flex" flexWrap="wrap" gap={1} justifyContent="flex-end">
            <MDButton
              id="tour-quan-ly-hop-tac-xa-xuat-excel"
              variant="outlined"
              color="dark"
              size="small"
              onClick={xuatExcel}
              disabled={dangXuatExcel || dangTai || danhSach.length === 0}
            >
              <Icon sx={{ mr: 0.75 }}>download</Icon>
              {dangXuatExcel ? "Đang xuất..." : "Xuất Excel"}
            </MDButton>
            <MDButton
              id="tour-quan-ly-hop-tac-xa-nut-them"
              variant="gradient"
              color="info"
              onClick={() => {
                setLoi("");
                setThongBao("");
                setModal({ ...RONG });
              }}
            >
              <Icon sx={{ mr: 0.75 }}>add</Icon>
              Thêm hợp tác xã
            </MDButton>
          </MDBox>
        </DauTrang>

        {loi && (
          <MDBox mb={2}>
            <MDAlert color="error">{loi}</MDAlert>
          </MDBox>
        )}
        {thongBao && (
          <MDBox mb={2}>
            <MDAlert color="success">{thongBao}</MDAlert>
          </MDBox>
        )}

        <Card id="tour-quan-ly-hop-tac-xa-danh-sach">
          <MDBox
            px={2.5}
            py={2}
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
          >
            <MDInput
              id="tour-quan-ly-hop-tac-xa-tim"
              label="Tìm hợp tác xã"
              placeholder="Mã HTX, tên, người đại diện, SĐT, email"
              value={timKiem}
              onChange={(e) => setTimKiem(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon fontSize="small">search</Icon>
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: "100%", sm: "22rem" } }}
            />
            <MDTypography variant="caption" color="text">
              {danhSachHienThi.length} hợp tác xã
            </MDTypography>
          </MDBox>

          {dangTai ? (
            <MDBox px={2.5} pb={3}>
              <MDTypography variant="button" color="text">
                Đang tải...
              </MDTypography>
            </MDBox>
          ) : danhSachHienThi.length === 0 ? (
            <MDBox px={2.5} pb={4} pt={1} textAlign="center">
              <Icon sx={({ palette }) => ({ color: palette.grey[400], fontSize: "2.5rem" })}>
                groups
              </Icon>
              <MDTypography variant="button" fontWeight="medium" display="block" mt={1}>
                {danhSach.length === 0
                  ? "Chưa có hợp tác xã nào."
                  : "Không tìm thấy hợp tác xã phù hợp."}
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                {danhSach.length === 0
                  ? "Bấm “Thêm hợp tác xã” để tạo hồ sơ đầu tiên."
                  : "Thử bớt từ khoá, hoặc tìm theo Mã HTX / số điện thoại."}
              </MDTypography>
            </MDBox>
          ) : (
            <MDBox sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={oTieuDeCot}>Tên hợp tác xã</TableCell>
                    <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Mã HTX</TableCell>
                    <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Người đại diện</TableCell>
                    <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>SĐT</TableCell>
                    <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Địa chỉ</TableCell>
                    <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Email</TableCell>
                    <TableCell align="right" sx={oTieuDeCot}>
                      Thao tác
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {danhSachHienThi.map((htx) => (
                    <TableRow
                      key={htx.id}
                      sx={({ palette }) => ({
                        transition: "background-color 160ms ease",
                        "&:hover": { backgroundColor: palette.grey[100] },
                      })}
                    >
                      <TableCell>
                        <MDTypography
                          variant="button"
                          fontWeight="medium"
                          display="block"
                          sx={chuXuongDong}
                        >
                          {htx.tenHopTacXa}
                        </MDTypography>
                        <ThongTinPhu htx={htx} />
                      </TableCell>
                      <TableCell sx={AN_DUOI_MD}>
                        {/* Cột Mã ẩn dưới md — mã vẫn hiện trong ThongTinPhu trên mobile. */}
                        <MDTypography
                          variant="button"
                          fontWeight={htx.maHopTacXa ? "medium" : "regular"}
                          color={htx.maHopTacXa ? "dark" : "text"}
                          sx={chuXuongDong}
                        >
                          {htx.maHopTacXa || "—"}
                        </MDTypography>
                      </TableCell>
                      <TableCell sx={AN_DUOI_MD}>{htx.nguoiDaiDien || "—"}</TableCell>
                      <TableCell sx={AN_DUOI_MD}>{htx.sdt || "—"}</TableCell>
                      <TableCell sx={AN_DUOI_MD}>{htx.diaChi || "—"}</TableCell>
                      <TableCell sx={AN_DUOI_MD}>{htx.email || "—"}</TableCell>
                      <TableCell align="right">
                        <MDBox display="flex" flexWrap="wrap" justifyContent="flex-end" gap={0.5}>
                          <MDButton
                            variant="text"
                            color="info"
                            size="small"
                            onClick={() => {
                              setLoi("");
                              setModal(htx);
                            }}
                          >
                            Sửa
                          </MDButton>
                          {!htx.maHopTacXa && (
                            <Tooltip
                              title={
                                htx.tinhMa && htx.xaMa
                                  ? ""
                                  : "Chọn lại Tỉnh và Xã (bấm Sửa) trước khi cấp mã"
                              }
                            >
                              <span>
                                <MDButton
                                  variant="text"
                                  color="dark"
                                  size="small"
                                  disabled={dangCapMaId === htx.id || !htx.tinhMa || !htx.xaMa}
                                  onClick={() => capMa(htx)}
                                >
                                  {dangCapMaId === htx.id ? "Đang cấp..." : "Cấp mã"}
                                </MDButton>
                              </span>
                            </Tooltip>
                          )}
                          <MDButton
                            variant="text"
                            color="error"
                            size="small"
                            disabled={dangXoaId === htx.id}
                            onClick={() => xoa(htx)}
                          >
                            {dangXoaId === htx.id ? "Đang xoá..." : "Xoá"}
                          </MDButton>
                        </MDBox>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </MDBox>
          )}
        </Card>
      </MDBox>
      <Footer />

      <Dialog
        open={!!modal}
        onClose={() => setModal(null)}
        fullWidth
        maxWidth="md"
        fullScreen={fullManHinh}
        PaperProps={oDialogPaperFormDai}
      >
        <DialogTitle component="div" sx={{ pb: 0.5, pr: 2, flexShrink: 0, ...chuXuongDong }}>
          <NhanDau>{modal?.id ? "Cập nhật" : "Thêm mới"}</NhanDau>
          <MDTypography variant="h5" component="h2" sx={chuXuongDong}>
            Hợp tác xã
          </MDTypography>
        </DialogTitle>
        {modal && (
          <MDBox component="form" onSubmit={luu} sx={oDialogFormFlex}>
            <DialogContent sx={oDialogContentFormDai}>
              {loi && (
                <MDBox mb={2}>
                  <MDAlert color="error">{loi}</MDAlert>
                </MDBox>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <MDInput
                    label="Tên hợp tác xã"
                    value={modal.tenHopTacXa}
                    onChange={(e) => setModal({ ...modal, tenHopTacXa: e.target.value })}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <MDInput
                    label="Người đại diện"
                    value={modal.nguoiDaiDien}
                    onChange={(e) => setModal({ ...modal, nguoiDaiDien: e.target.value })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TieuDeNhom
                    moTa={
                      modal.id
                        ? "Sửa được, nhưng Mã HTX đã cấp giữ nguyên — không sinh lại."
                        : "Tỉnh và Xã (mã hành chính) dùng để sinh Mã HTX, bắt buộc chọn."
                    }
                  >
                    Địa bàn
                  </TieuDeNhom>
                </Grid>
                <ChonDiaChi
                  tinh={modal.tinh ?? ""}
                  xa={modal.xa ?? ""}
                  onChange={({ tinh, xa, tinhMa, xaMa }) =>
                    setModal({ ...modal, tinh, xa, tinhMa, xaMa })
                  }
                  batBuoc={!modal.id}
                />
                {modal.id ? (
                  <Grid item xs={12}>
                    <MDInput
                      label="Mã HTX"
                      value={modal.maHopTacXa || "Chưa có mã"}
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      helperText="Mã sinh tự động một lần lúc tạo, không sửa được."
                    />
                  </Grid>
                ) : null}
                <Grid item xs={12}>
                  <TieuDeNhom>Liên hệ</TieuDeNhom>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDInput
                    label="Số điện thoại"
                    value={modal.sdt}
                    onChange={(e) => setModal({ ...modal, sdt: e.target.value })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDInput
                    type="email"
                    label="Email"
                    value={modal.email}
                    onChange={(e) => setModal({ ...modal, email: e.target.value })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <MDInput
                    label="Địa chỉ"
                    value={modal.diaChi}
                    onChange={(e) => setModal({ ...modal, diaChi: e.target.value })}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={oDialogActionsFormDai}>
              <MDButton variant="outlined" color="secondary" onClick={() => setModal(null)}>
                Huỷ
              </MDButton>
              <MDButton type="submit" variant="gradient" color="info" disabled={dangLuu}>
                {dangLuu ? "Đang lưu..." : "Lưu"}
              </MDButton>
            </DialogActions>
          </MDBox>
        )}
      </Dialog>
    </DashboardLayout>
  );
}

export default QuanLyHopTacXa;
